import { ErrorHandler, inject, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AlertModule } from 'ngx-bootstrap/alert';
import { SharedModule } from './shared/shared.module';
import { lastValueFrom } from 'rxjs';
import { CurrentUserService } from './services/current-user.service';
import { ImpersonationService } from './services/impersonation.service';

import { AppComponent } from './app.component';
import { LegacyAppComponent } from './legacy-app/legacy-app.component';
import { AppRoutingModule } from "./app-routing.module";
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LoginComponent } from './login/login.component';
import { SignupComponent } from './signup/signup.component';
import { GdprAcceptanceComponent } from './gdpr_acceptance/gdpr_acceptance.component';
import { PasswordResetComponent } from './password-reset/password-reset.component';
import { NewTicketComponent } from './new-ticket/new-ticket.component';
import { GraphQLModule } from './graphql.module';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { DomainsComponent } from './ccp/domains/domains.component';
import { UsersComponent } from './ccp/users/users.component';
import { AccountsComponent } from './ccp/accounts/accounts.component';
import { PasswordModule } from 'primeng/password';
import { InputTextModule } from 'primeng/inputtext';
import { ToastrModule } from 'ngx-toastr';
import { PaginatedTableModule } from './paginated-table/paginated-table.module';
import { LogbookEntriesComponent } from "./ccp/logbook-entries/logbook-entries.component";
import { HeaderComponent } from "./header/header.component";
import { FooterComponent } from "./footer/footer.component";
import { ToolbarModule } from 'primeng/toolbar';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { AutoFocusModule } from 'primeng/autofocus';
import { DialogModule as PDialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { BrandablePreset } from './BrandablePreset'
import { GlobalErrorHandler } from './error-handler/global-error-handler';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { CardModule } from 'primeng/card';
import { MessageModule } from 'primeng/message';
import { Tabs, TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';
import { CountrySelectorComponent } from './shared/country-selector/country-selector.component';
import { SessionDataService } from './services/session-data.service';
import { NgxTurnstileModule } from 'ngx-turnstile';

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    SignupComponent,
    DomainsComponent,
    UsersComponent,
    LegacyAppComponent,
    LogbookEntriesComponent,
    PasswordResetComponent,
    GdprAcceptanceComponent,
    NewTicketComponent
  ],
  bootstrap: [AppComponent],
  imports: [
    AccountsComponent,
    AppRoutingModule,
    AutoFocusModule,
    BrowserModule,
    BrowserAnimationsModule,
    CardModule,
    CountrySelectorComponent,
    SharedModule,
    ReactiveFormsModule,
    FormsModule,
    AlertModule.forRoot(),
    TooltipModule.forRoot(),
    GraphQLModule,
    PasswordModule,
    InputTextModule,
    ToastrModule.forRoot({ closeButton: true, timeOut: 8000 }),
    PaginatedTableModule,
    HeaderComponent,
    FooterComponent,
    ToolbarModule,
    ButtonModule,
    CheckboxModule,
    PDialogModule,
    DynamicDialogModule,
    MessageModule,
    SelectModule,
    TabsModule,
    TextareaModule,
    NgxTurnstileModule
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: BrandablePreset,
        options: {
          darkModeSelector: false
        }
      }
    }),
    provideAppInitializer(async () => {
      const currentUserService = inject(CurrentUserService);
      const impersonationService = inject(ImpersonationService);
      const sessionDataService = inject(SessionDataService);

      try {
        const user = await lastValueFrom(currentUserService.getCurrentUser());
        currentUserService.updateCurrentUser(user);

        await sessionDataService.refresh();

        // Initialize impersonation service after user is loaded
        // This ensures the impersonation status is determined before components load
        // The service is already injected above, so it will initialize automatically
      } catch (error) {
        // Silently handle authentication failures during app initialization
        // This allows the app to load normally for unauthenticated users
        console.log('User not authenticated during app initialization, continuing...', error);
      }
    }),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    DialogService
  ]
})
export class AppModule { }
